/** Lightweight CSV helpers for bulk import/export + downloadable templates. */

export interface CsvColumn {
  /** Payload key sent to the API and used to match template/import headers. */
  key: string;
  /** Human-readable column header shown in exports and templates. */
  label: string;
  /** Source key when reading existing rows for export (defaults to `key`). */
  exportKey?: string;
  /** Example value placed in the downloadable template's sample row. */
  sample?: string;
}

const BOM = '﻿';

function escapeCell(value: unknown): string {
  const s = value === null || value === undefined ? '' : String(value);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Trigger a client-side file download of `content`. */
export function download(filename: string, content: string, mime = 'text/csv;charset=utf-8;'): void {
  const blob = new Blob([BOM + content], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** Serialize existing rows to CSV using the given column spec. */
export function toCsv(columns: CsvColumn[], rows: Record<string, unknown>[]): string {
  const header = columns.map((c) => escapeCell(c.label)).join(',');
  const lines = rows.map((row) => columns.map((c) => escapeCell(row[c.exportKey ?? c.key])).join(','));
  return [header, ...lines].join('\n');
}

/** A header row plus a single example row so users know the expected format. */
export function templateCsv(columns: CsvColumn[]): string {
  const header = columns.map((c) => escapeCell(c.label)).join(',');
  const sample = columns.map((c) => escapeCell(c.sample ?? '')).join(',');
  return [header, sample].join('\n');
}

/** Split raw CSV text into records, honouring quoted cells with commas/newlines. */
function splitRecords(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  const s = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (quoted) {
      if (ch === '"') {
        if (s[i + 1] === '"') { cell += '"'; i++; } else { quoted = false; }
      } else { cell += ch; }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === ',') {
      row.push(cell); cell = '';
    } else if (ch === '\n') {
      row.push(cell); rows.push(row); row = []; cell = '';
    } else {
      cell += ch;
    }
  }
  if (cell !== '' || row.length > 0) { row.push(cell); rows.push(row); }
  return rows;
}

/**
 * Parse CSV text into row objects keyed by the column spec's `key`.
 * Headers are matched to columns by label (case-insensitive) or by key, so a
 * file exported from the app re-imports cleanly. Empty cells are omitted so the
 * server's schema defaults apply.
 */
export function parseCsv(text: string, columns: CsvColumn[]): Record<string, string>[] {
  const stripped = text.replace(/^﻿/, '');
  const records = splitRecords(stripped).filter((r) => r.some((c) => c.trim() !== ''));
  if (records.length < 2) return [];
  const headers = records[0].map((h) => h.trim().toLowerCase());
  const lookup = new Map<string, string>();
  for (const c of columns) {
    lookup.set(c.label.toLowerCase(), c.key);
    lookup.set(c.key.toLowerCase(), c.key);
  }
  const keyForCol = headers.map((h) => lookup.get(h) ?? null);
  const out: Record<string, string>[] = [];
  for (let i = 1; i < records.length; i++) {
    const cells = records[i];
    const obj: Record<string, string> = {};
    for (let j = 0; j < cells.length; j++) {
      const key = keyForCol[j];
      if (!key) continue;
      const val = (cells[j] ?? '').trim();
      if (val !== '') obj[key] = val;
    }
    if (Object.keys(obj).length > 0) out.push(obj);
  }
  return out;
}

/* ------------------------------------------------------------------ *
 * Column specs — shared by template, import (key -> API) and export.  *
 * ------------------------------------------------------------------ */

export const STUDENT_CSV_COLUMNS: CsvColumn[] = [
  { key: 'firstName', label: 'First Name', sample: 'Arjun' },
  { key: 'lastName', label: 'Last Name', sample: 'Mehta' },
  { key: 'gender', label: 'Gender', sample: 'Male' },
  { key: 'dateOfBirth', label: 'Date of Birth', sample: '2003-05-14' },
  { key: 'nationality', label: 'Nationality', sample: 'Indian' },
  { key: 'phone', label: 'Phone', sample: '+91 98765 43210' },
  { key: 'alternatePhone', label: 'Alternate Phone', sample: '' },
  { key: 'email', label: 'Email', sample: 'arjun@email.com' },
  { key: 'currentCity', label: 'Current City', sample: 'Hyderabad' },
  { key: 'parentName', label: 'Parent Name', sample: 'Rajiv Mehta' },
  { key: 'parentPhone', label: 'Parent Phone', sample: '+91 90000 00000' },
  { key: 'destinationCountry', label: 'Destination Country', sample: 'United Kingdom' },
  { key: 'preferredIntake', label: 'Preferred Intake', sample: 'Sep 2026' },
  { key: 'studyLevel', label: 'Study Level', sample: 'Postgraduate' },
  { key: 'preferredCourse', label: 'Preferred Course', sample: 'MSc Computer Science' },
  { key: 'budget', label: 'Budget', sample: '25-35 lakh' },
  { key: 'fundingSource', label: 'Funding Source', sample: 'Self-funded' },
  { key: 'highestQualification', label: 'Highest Qualification', sample: 'B.Tech CSE' },
  { key: 'qualificationGrade', label: 'Grade / Percentage', sample: '8.4 CGPA' },
  { key: 'englishTest', label: 'English Test', sample: 'IELTS' },
  { key: 'englishTestScore', label: 'English Test Score', sample: '7.5' },
  { key: 'passportNumber', label: 'Passport Number', sample: '' },
  { key: 'passportExpiry', label: 'Passport Expiry', sample: '' },
  { key: 'source', label: 'Lead Source', sample: 'Referral' },
  { key: 'status', label: 'Status', sample: 'lead' },
  { key: 'notes', label: 'Notes', sample: '' },
];

export const INSTITUTION_CSV_COLUMNS: CsvColumn[] = [
  { key: 'name', label: 'Name', sample: 'University of Manchester' },
  { key: 'type', label: 'Type', sample: 'University' },
  { key: 'country', label: 'Country', sample: 'United Kingdom' },
  { key: 'state', label: 'State / Region', sample: 'England' },
  { key: 'city', label: 'City', sample: 'Manchester' },
  { key: 'website', label: 'Website', sample: 'www.manchester.ac.uk' },
  { key: 'ranking', label: 'Ranking', sample: '#32 QS' },
  { key: 'submissionMethod', label: 'Submission Method', exportKey: 'submission_method', sample: 'Direct Portal' },
  { key: 'partnerStatus', label: 'Partner Status', exportKey: 'partner_status', sample: 'active' },
  { key: 'contactName', label: 'Contact Name', exportKey: 'contact_name', sample: 'Jane Smith' },
  { key: 'contactEmail', label: 'Contact Email', exportKey: 'contact_email', sample: 'jane@manchester.ac.uk' },
  { key: 'contactPhone', label: 'Contact Phone', exportKey: 'contact_phone', sample: '' },
  { key: 'contactPosition', label: 'Contact Position', exportKey: 'contact_position', sample: 'Regional Manager' },
  { key: 'commissionRate', label: 'Commission', exportKey: 'commission_rate', sample: '15%' },
  { key: 'applicationFee', label: 'Application Fee', exportKey: 'application_fee', sample: '50 GBP' },
  { key: 'intakes', label: 'Intakes', sample: 'Sep, Jan' },
  { key: 'accreditation', label: 'Accreditation', sample: 'Russell Group' },
  { key: 'notes', label: 'Notes', sample: '' },
];

export const APPLICATION_CSV_COLUMNS: CsvColumn[] = [
  { key: 'studentName', label: 'Student', sample: 'Arjun Mehta' },
  { key: 'institutionName', label: 'Institution', sample: 'University of Manchester' },
  { key: 'courseName', label: 'Course', sample: 'MSc Computer Science' },
  { key: 'intake', label: 'Intake', sample: 'Sep 2026' },
  { key: 'stage', label: 'Stage', sample: 'Documents' },
  { key: 'priority', label: 'Priority', sample: 'Medium' },
  { key: 'applicationReference', label: 'Application Reference', sample: '' },
  { key: 'deadline', label: 'Deadline', sample: '2026-06-30' },
  { key: 'offerType', label: 'Offer Type', sample: '' },
  { key: 'scholarship', label: 'Scholarship', sample: '' },
];

export const COURSE_CSV_COLUMNS: CsvColumn[] = [
  { key: 'institutionName', label: 'Institution', sample: 'University of Manchester' },
  { key: 'name', label: 'Course Name', sample: 'MSc Computer Science' },
  { key: 'discipline', label: 'Discipline', sample: 'Computer Science' },
  { key: 'studyLevel', label: 'Level', sample: 'Postgraduate' },
  { key: 'durationMonths', label: 'Duration (months)', sample: '12' },
  { key: 'intake', label: 'Intake', sample: 'Sep 2026' },
  { key: 'tuitionAmount', label: 'Tuition', sample: '28000' },
  { key: 'currency', label: 'Currency', sample: 'GBP' },
  { key: 'applicationDeadline', label: 'Application Deadline', sample: '2026-06-30' },
  { key: 'applicationFee', label: 'Application Fee', sample: '50 GBP' },
  { key: 'englishRequirement', label: 'English Requirement', sample: 'IELTS 6.5' },
  { key: 'scholarship', label: 'Scholarship', sample: 'Up to 5,000 GBP merit' },
  { key: 'campusCity', label: 'Campus City', sample: 'Manchester' },
  { key: 'deliveryMode', label: 'Delivery Mode', sample: 'On campus' },
];
